import {
  CMS_THEME_DEFAULT_ID,
  CMS_THEMES,
  isCmsThemeAvailable,
  resolveCmsThemeMeta,
} from './cmsThemeRegistry'

/** 用户固定主题 id（仅「主动选定」后写入） */
export const CMS_THEME_STORAGE_KEY = 'aether-cms-ui-theme'
/** 值为 '1' 表示用户曾主动选定主题，不再随会话随机 */
export const CMS_THEME_PINNED_KEY = 'aether-cms-ui-theme-pinned'
/** 未固定时，当前浏览器会话内的随机主题（刷新不变，新开标签/会话重新随机） */
export const CMS_THEME_SESSION_KEY = 'aether-cms-ui-theme-session'

/** 下拉菜单：切回「每次打开随机」模式的 command 值 */
export const CMS_THEME_RANDOM_COMMAND = '__random__'

/**
 * 从未固定池中随机抽取可用主题 id。
 * 用于未 pin 用户每次新会话开屏展示不同风格。
 */
export function pickRandomCmsThemeId() {
  const pool = CMS_THEMES.filter((item) => item.available)
  if (!pool.length) {
    return CMS_THEME_DEFAULT_ID
  }
  const index = Math.floor(Math.random() * pool.length)
  return pool[index].id
}

/** 用户是否已通过主题选择器主动固定主题 */
export function isCmsThemePinned() {
  try {
    return localStorage.getItem(CMS_THEME_PINNED_KEY) === '1'
  } catch (_error) {
    return false
  }
}

/**
 * 将主题 id 挂到 documentElement，供 themes/*.css 通过 [data-cms-theme] 注入 token。
 * persist=true 时写入 localStorage 并标记 pinned；否则仅改 DOM，不覆盖用户固定偏好。
 */
export function applyCmsTheme(themeId, options = {}) {
  const { persist = false } = options
  const meta = resolveCmsThemeMeta(themeId)
  const resolvedId = meta.id
  document.documentElement.setAttribute('data-cms-theme', resolvedId)
  document.documentElement.style.colorScheme = 'light'
  if (persist) {
    try {
      localStorage.setItem(CMS_THEME_STORAGE_KEY, resolvedId)
      localStorage.setItem(CMS_THEME_PINNED_KEY, '1')
    } catch (_error) {
      /* 隐私模式等场景忽略持久化失败 */
    }
  }
  return resolvedId
}

export function getCmsThemeId() {
  return document.documentElement.getAttribute('data-cms-theme') || CMS_THEME_DEFAULT_ID
}

/**
 * 应用启动：已固定 → 读 localStorage；未固定 → 本会话随机（sessionStorage 复用，刷新不变）。
 */
export function initCmsTheme() {
  if (isCmsThemePinned()) {
    let stored = CMS_THEME_DEFAULT_ID
    try {
      stored = localStorage.getItem(CMS_THEME_STORAGE_KEY) || CMS_THEME_DEFAULT_ID
    } catch (_error) {
      stored = CMS_THEME_DEFAULT_ID
    }
    if (!isCmsThemeAvailable(stored)) {
      stored = CMS_THEME_DEFAULT_ID
    }
    return applyCmsTheme(stored, { persist: false })
  }

  let sessionTheme = null
  try {
    sessionTheme = sessionStorage.getItem(CMS_THEME_SESSION_KEY)
  } catch (_error) {
    sessionTheme = null
  }
  if (!sessionTheme || !isCmsThemeAvailable(sessionTheme)) {
    sessionTheme = pickRandomCmsThemeId()
    try {
      sessionStorage.setItem(CMS_THEME_SESSION_KEY, sessionTheme)
    } catch (_error) {
      /* ignore */
    }
  }
  return applyCmsTheme(sessionTheme, { persist: false })
}

/** 用户主动选定某主题：固定到 localStorage，后续启动不再随机 */
export function setCmsThemePinned(themeId) {
  if (!isCmsThemeAvailable(themeId)) {
    return getCmsThemeId()
  }
  try {
    sessionStorage.removeItem(CMS_THEME_SESSION_KEY)
  } catch (_error) {
    /* ignore */
  }
  return applyCmsTheme(themeId, { persist: true })
}

/** 恢复「每次打开随机」：清除 pin，并立即抽一个新主题作为本会话主题 */
export function setCmsThemeRandomMode() {
  try {
    localStorage.removeItem(CMS_THEME_PINNED_KEY)
    localStorage.removeItem(CMS_THEME_STORAGE_KEY)
    sessionStorage.removeItem(CMS_THEME_SESSION_KEY)
  } catch (_error) {
    /* ignore */
  }
  const nextId = pickRandomCmsThemeId()
  try {
    sessionStorage.setItem(CMS_THEME_SESSION_KEY, nextId)
  } catch (_error) {
    /* ignore */
  }
  return applyCmsTheme(nextId, { persist: false })
}

/** 顶栏选择器入口：具体主题 → 固定；随机 command → 取消固定 */
export function setCmsTheme(themeId) {
  if (themeId === CMS_THEME_RANDOM_COMMAND) {
    return setCmsThemeRandomMode()
  }
  return setCmsThemePinned(themeId)
}
